/** BD › Research (crx-be /bd/research): research pages, their files and labelled answers. */

export type ResearchKind = "work" | "company";
export type ResearchLinkType = "opportunity" | "client" | "lead";
export type ResearchFileKind = "pdf" | "word" | "sheet" | "text" | "paste" | "web";
/** Where a part of an answer came from: the page's files, CRX, a web page on the page, or the AI's own knowledge ("check it"). */
export type ResearchSource = "files" | "crx" | "web" | "general";

/** A web search result from Find sources (sent back as it is to add the page). */
export interface WebSource { title: string; url: string; snippet: string; content?: string; publishedAt?: string | null; }

export type Cell = string | number;

export interface ResearchSheet { name: string; rows: Cell[][]; }

export interface ResearchPart { from: ResearchSource; text: string; }

export interface ResearchTable { title: string; columns: string[]; rows: Cell[][]; }

export interface ResearchChart {
    type: "bars" | "columns";
    title: string;
    /** "$", "%", "" (a count) or a currency code ("JOD") — Ask CRX's price charts. */
    unit: string;
    items: Array<{ label: string; value: number }>;
}

export interface ResearchFlow {
    type: "flow";
    title: string;
    nodes: Array<{ id: string; label: string; kind: "start" | "step" | "decision" | "end" }>;
    edges: Array<{ from: string; to: string; label: string }>;
}

export interface ResearchTimeline {
    type: "timeline";
    title: string;
    items: Array<{ label: string; start: string; end?: string }>;
}

export interface ResearchOrgNode { name: string; role: string; children: ResearchOrgNode[]; }

export interface ResearchOrg { type: "org"; title: string; root: ResearchOrgNode; }

export type ResearchDiagram = ResearchFlow | ResearchTimeline | ResearchOrg;

/**
 * A range check on an answer's table: where one figure sits among a column's
 * numbers, worked out by CRX (Excel's QUARTILE method), never by the AI.
 */
export interface ResearchRange {
    column: string;
    label: string;
    value: number;
    /** The column's numbers that were used, smallest first. */
    values: number[];
    /** Rows left out: no number, or more than one. */
    skipped: number;
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
    position: "below" | "inside" | "above";
}

/** A range check drawn as a picture. */
export interface ResearchRangeFigure { type: "range"; title: string; range: ResearchRange; }

/** A chart or a diagram: anything drawn as a picture. */
export type ResearchFigureSpec = ResearchChart | ResearchDiagram | ResearchRangeFigure;

export interface ResearchAnswer {
    parts: ResearchPart[];
    table: ResearchTable | null;
    chart: ResearchChart | null;
    diagram: ResearchDiagram | null;
    range?: ResearchRange | null;
    /** Changed by hand on the page (the AI's original isn't kept). */
    tableEdited?: boolean;
}

export interface ResearchProfile {
    overview: string;
    numbers: Array<{ label: string; value: string }>;
    people: Array<{ name: string; role: string; reportsTo: string }>;
    fit: Array<{ service: string; match: "Yes" | "Partly" | "No"; why: string }>;
    risks: string[];
    questions: string[];
    known: string;
}

export interface ResearchLink {
    type: ResearchLinkType;
    id: number;
    name: string;
    reference: string | null;
    url: string;
    clientId?: number | null;
    clientName?: string | null;
}

export interface ResearchFile {
    researchFileId: number;
    name: string;
    kind: ResearchFileKind;
    parts: string | null;
    fileType: string | null;
    fileSize: number | null;
    hasFile: boolean;
    truncated: boolean;
    /** Web sources: the page's address. */
    sourceUrl: string | null;
    /** Public material: its amounts stay visible to the AI (names are still masked). */
    publicFigures: boolean;
    preview: string;
    sheets: ResearchSheet[] | null;
    createdByName: string | null;
    createdAt: string | null;
}

export interface ResearchMessage {
    researchMessageId: number;
    question: string;
    answer: ResearchAnswer;
    masked: string | null;
    nameShown: boolean;
    aiModel: string | null;
    createdById: number | null;
    createdByName: string | null;
    createdAt: string | null;
}

export interface Research {
    researchId: number;
    title: string;
    kind: ResearchKind;
    link: ResearchLink | null;
    showName: boolean;
    profile: ResearchProfile | null;
    profileAt: string | null;
    fileCount: number;
    answerCount: number;
    createdById: number | null;
    createdByName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    files?: ResearchFile[];
    messages?: ResearchMessage[];
}

export interface ResearchView {
    research: Research;
    can: { edit: boolean; delete: boolean };
}

export interface ResearchLinkOption { type: ResearchLinkType; id: number; name: string; reference: string | null; sub: string | null; }

export interface CompanyMatch { type: "client" | "provider" | "organization" | "lead"; id: number; name: string; reference: string | null; url: string; }

/** How each kind of linked record is named on screen. */
export const LINK_LABEL: Record<ResearchLinkType, string> = { opportunity: "Opportunity", client: "Client", lead: "Lead" };

/** The label, icon and chip tone for each answer source. */
export const SOURCE_LABEL: Record<ResearchSource, { label: string; icon: string; tone: "neutral" | "success" | "info" | "warning"; tooltip: string }> = {
    files: { label: "From your files", icon: "file-earmark-text", tone: "neutral", tooltip: "Taken from the files and notes on this page." },
    crx: { label: "From CRX", icon: "database", tone: "success", tooltip: "Taken from CRX: the linked record or our company facts in BD settings." },
    web: { label: "From the web", icon: "globe2", tone: "warning", tooltip: "Taken from a web page added to this page with Find sources. Public, but check it; the AI gives the page's address when it can." },
    general: { label: "AI's own knowledge · check it", icon: "stars", tone: "info", tooltip: "From the AI's general knowledge, not from your files or CRX. It can be wrong or out of date, so check it before you rely on it." },
};

/** Ready-made starts for the question box. */
export const MAKE_STARTERS: Array<{ icon: string; label: string; text: string }> = [
    { icon: "table", label: "Table", text: "Make a table of " },
    { icon: "bar-chart", label: "Chart", text: "Make a chart of " },
    { icon: "diagram-3", label: "Flowchart", text: "Draw a flowchart of " },
    { icon: "calendar-range", label: "Timeline", text: "Draw a timeline of " },
    { icon: "people", label: "Org chart", text: "Draw an org chart of " },
];
